import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Fab,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  InputAdornment,
  IconButton,
  Paper,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import GroupIcon from '@mui/icons-material/Group';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TableRowsIcon from '@mui/icons-material/TableRows';
import BarChartIcon from '@mui/icons-material/BarChart';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TodayIcon from '@mui/icons-material/Today';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useNavigate } from 'react-router-dom';
import SearchBar from '../../components/SearchBar/SearchBar';
import PlayerList from '../../components/PlayerList/PlayerList';
import PlayerTable from '../../components/PlayerTable/PlayerTable';
import PlayerBuyInChart from '../../components/PlayerBuyInChart/PlayerBuyInChart';
import AddPlayerDialog from '../../components/AddPlayerDialog/AddPlayerDialog';
import ImportMasterPlayersDialog from '../../components/ImportMasterPlayersDialog/ImportMasterPlayersDialog';
import CSVDownloadButton from '../../components/CSVDownloadButton/CSVDownloadButton';
import { usePlayer } from '../../context/PlayerContext';
import { useExpense } from '../../context/ExpenseContext';
import { useCSVDownload } from '../../hooks/useCSVDownload';
import './Dashboard.scss';

const Dashboard = ({ sessionName = null }) => {
  const navigate = useNavigate();
  const { players, getTotalBuyIns, getTotalCashOuts, getNetProfit, getFilteredPlayers } = usePlayer();
  const { getTotalExpenses } = useExpense();
  const { downloadAllPlayersCSV, downloadFilteredPlayersCSV } = useCSVDownload(sessionName);
  const [addPlayerOpen, setAddPlayerOpen] = useState(false);
  const [importPlayersOpen, setImportPlayersOpen] = useState(false);
  
  // Initialize viewMode based on screen size - default to table on mobile (< 768px)
  const getInitialViewMode = () => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      return 'table';
    }
    return 'table';
  };
  
  const [viewMode, setViewMode] = useState(getInitialViewMode);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [speedDialOpen, setSpeedDialOpen] = useState(false);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getActivePlayersCount = () => {
    return players.filter(player => player.totalBuyIn > 0).length;
  };

  const handleDownloadCSV = () => {
    const filteredPlayers = getFilteredPlayers();
    if (filteredPlayers.length < players.length && filteredPlayers.length > 0) {
      downloadFilteredPlayersCSV(filteredPlayers);
    } else {
      downloadAllPlayersCSV();
    }
  };

  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  const handleExpensesCardClick = () => {
    navigate('/expenses');
  };

  const handleDateChange = (event) => {
    setSelectedDate(event.target.value);
  };

  const formatDateDisplay = () => {
    const date = new Date(selectedDate + 'T00:00:00'); // Add time to avoid timezone issues
    
    // Format as "Monday, Jan 15, 2024" to keep it on one line
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const totalBuyIns = getTotalBuyIns();
  const totalCashOuts = getTotalCashOuts();
  const netProfit = getNetProfit();
  
  // Bank Balance logic: if Total Buy-ins > Total Cash Outs, it's red (bank loss), otherwise green (bank profit)
  const isBankLoss = totalBuyIns > totalCashOuts;
  const bankBalance = Math.abs(netProfit);

  return (
    <Box className="dashboard fade-in">
      {/* Date Selector Section */}
      <Box className="date-selector-section">
        <Paper className="date-selector-container" elevation={2}>
          <Box className="date-selector-content">
            <Box className="date-display">
              <CalendarTodayIcon className="date-icon" />
              <Typography variant="h5" className="selected-date">
                {formatDateDisplay()}
              </Typography>
            </Box>
            
            <TextField
              type="date"
              value={selectedDate}
              onChange={handleDateChange}
              size="small"
              className="custom-date-picker"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarTodayIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Paper>
      </Box>

      {/* Hero Section */}
      <Box className="dashboard-hero">
        <Typography variant="h2" className="hero-title">
          Poker Buy-in Tracker
        </Typography>
        <Typography variant="h6" color="text.secondary" className="hero-subtitle">
          Keep track of all your poker buy-ins, cash outs, and expenses in one place
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} className="stats-section">
        <Grid item xs={12} sm={6} md={2.4}>
          <Card className="stat-card">
            <CardContent>
              <Box className="stat-content">
                <GroupIcon className="stat-icon" />
                <Box>
                  <Typography variant="h4" className="stat-number">
                    {players.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Players
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card className="stat-card highlight">
            <CardContent>
              <Box className="stat-content">
                <Box className="currency-icon blue">$</Box>
                <Box>
                  <Typography variant="h4" className="stat-number">
                    {formatCurrency(totalBuyIns).replace('$', '')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Buy-ins
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card className="stat-card success">
            <CardContent>
              <Box className="stat-content">
                <AccountBalanceWalletIcon className="stat-icon success" />
                <Box>
                  <Typography variant="h4" className="stat-number">
                    {formatCurrency(totalCashOuts).replace('$', '')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Cash Outs
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card className={`stat-card ${isBankLoss ? 'loss' : 'profit'}`}>
            <CardContent>
              <Box className="stat-content">
                {isBankLoss ? (
                  <TrendingDownIcon className="stat-icon loss" />
                ) : (
                  <TrendingUpIcon className="stat-icon profit" />
                )}
                <Box>
                  <Typography variant="h4" className="stat-number">
                    {formatCurrency(bankBalance).replace('$', '')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Bank Balance
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card className="stat-card clickable" onClick={handleExpensesCardClick}>
            <CardContent>
              <Box className="stat-content">
                <ReceiptIcon className="stat-icon expense" />
                <Box>
                  <Typography variant="h4" className="stat-number">
                    {formatCurrency(getTotalExpenses()).replace('$', '')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Misc. Expenses
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action Section */}
      <Box className="action-section">
        <Box className="section-header">
          <Typography variant="h4" className="section-title">
            Players
          </Typography>
          <Box className="header-actions">
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              className="view-toggle"
              size="small"
            >
              <ToggleButton value="cards" aria-label="card view">
                <ViewModuleIcon />
              </ToggleButton>
              <ToggleButton value="table" aria-label="table view">
                <TableRowsIcon />
              </ToggleButton>
              <ToggleButton value="chart" aria-label="chart view">
                <BarChartIcon />
              </ToggleButton>
            </ToggleButtonGroup>
            <CSVDownloadButton
              onClick={handleDownloadCSV}
              disabled={players.length === 0}
              tooltip={players.length === 0 ? "No data to download" : "Download players data as CSV"}
              label="Export CSV"
              className="export-button"
            />
            <Button
              variant="outlined"
              startIcon={<GroupAddIcon />}
              onClick={() => setImportPlayersOpen(true)}
              className="import-button"
            >
              Import Players
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddPlayerOpen(true)}
              className="add-button"
            >
              Add Player
            </Button>
          </Box>
        </Box>
        
        <SearchBar />
            {viewMode === 'cards' ? <PlayerList /> : viewMode === 'table' ? <PlayerTable /> : <PlayerBuyInChart />}
      </Box>

      {/* Speed Dial for Mobile */}
      <SpeedDial
        ariaLabel="Player actions"
        className="speed-dial mobile-only"
        icon={<SpeedDialIcon />}
        onClose={() => setSpeedDialOpen(false)}
        onOpen={() => setSpeedDialOpen(true)}
        open={speedDialOpen}
        direction="up"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          '& .MuiSpeedDial-fab': {
            bgcolor: '#006aff',
            '&:hover': {
              bgcolor: '#0056cc',
            },
          },
        }}
      >
        <SpeedDialAction
          key="add-player"
          icon={<PersonAddIcon />}
          tooltipTitle="Add New Player"
          tooltipPlacement="left"
          onClick={() => {
            setSpeedDialOpen(false);
            setAddPlayerOpen(true);
          }}
          sx={{
            '& .MuiSpeedDialAction-fab': {
              bgcolor: '#4caf50',
              '&:hover': {
                bgcolor: '#388e3c',
              },
            },
          }}
        />
        <SpeedDialAction
          key="import-players"
          icon={<GroupAddIcon />}
          tooltipTitle="Import from Master List"
          tooltipPlacement="left"
          onClick={() => {
            setSpeedDialOpen(false);
            setImportPlayersOpen(true);
          }}
          sx={{
            '& .MuiSpeedDialAction-fab': {
              bgcolor: '#ff9800',
              '&:hover': {
                bgcolor: '#f57c00',
              },
            },
          }}
        />
      </SpeedDial>

      <AddPlayerDialog 
        open={addPlayerOpen}
        onClose={() => setAddPlayerOpen(false)}
      />

      <ImportMasterPlayersDialog
        open={importPlayersOpen}
        onClose={() => setImportPlayersOpen(false)}
      />
    </Box>
  );
};

export default Dashboard;